import { BasicInput, Modal } from '@fl/cmsch-fe-library';
import React, { useState, memo, useMemo, useCallback, FC, KeyboardEvent, ChangeEvent } from 'react';
import { Opt, none, optEmptyString } from 'ts-opt';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import styles from './styles.module.sass';
interface Props {
  /** Whether the modal is visible. */
  visible: boolean;
  /** Handles file-name submission. */
  onSubmit(fileName: string): void;
  /** Handles cancel and close. */
  onCancel(): void;
}
const maxFilenameLength = 100;
const defaultName: Opt<string> = none;
const ModalSaveScoringResultsBase: FC<Props> = props => {
  const {
    visible,
    onSubmit,
    onCancel
  } = props;
  const {
    t,
    tCommon
  } = useOurTranslation('mating');
  const [fileName, setFileName] = useState<Opt<string>>(defaultName);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [touched, setTouched] = useState<boolean>(false);
  const isInvalidLength = useMemo(() => fileName.orElse('').length > maxFilenameLength, [fileName]);
  const isError = touched && !isValid;
  const helpMessage: string = useMemo(() => isInvalidLength ? t('maxFileNameLength') : t('enterFileName'), [isInvalidLength, t]);
  const handleSubmit = useCallback(() => {
    if (!isError) {
      fileName.onSome(fileNamePresent => {
        onSubmit(fileNamePresent);
        setFileName(defaultName);
        setIsValid(false);
        setTouched(false);
      }).orCrash('should be able to send list name');
    }
  }, [isError, fileName, onSubmit]);
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = optEmptyString(event.target.value);
    setTouched(true);
    setFileName(value);
    setIsValid(value.nonEmpty && value.orElse('').length <= maxFilenameLength);
  }, []);
  const handleBlur = useCallback(() => {
    setTouched(true);
  }, []);
  const handleKeyUp = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);
  const handleCancel = useCallback(() => {
    setFileName(defaultName);
    setIsValid(false);
    setTouched(false);
    onCancel();
  }, [onCancel]);
  return <div onKeyUp={handleKeyUp} data-sentry-component="ModalSaveScoringResultsBase" data-sentry-source-file="index.tsx">
            <Modal title={t('savingMatingPlan')} visible={visible} onCancel={handleCancel} defaultFooter={{
      submitText: tCommon('save'),
      cancelText: tCommon('cancel'),
      onSubmit: handleSubmit,
      disabled: !isValid
    }} data-sentry-element="Modal" data-sentry-source-file="index.tsx">
                <Ant.Form.Item validateStatus={isError ? 'error' : undefined} hasFeedback help={isError ? helpMessage : null} label={t('term')} required data-test-id="field-scoringResultsFileName"
      // eslint-disable-next-line react/forbid-component-props
      className={styles.fileInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <BasicInput type="text" value={fileName} onFieldChange={handleChange} autoFocus onFieldBlur={handleBlur} data-sentry-element="BasicInput" data-sentry-source-file="index.tsx" />
                </Ant.Form.Item>
            </Modal>
        </div>;
};

/**
 * Memoized save-scoring-results modal for the result tab.
 *
 * @param props Visibility and submit/cancel handlers.
 * @returns The save-scoring-results modal.
 *
 * @example
 * <ModalSaveScoringResults
 *   visible={visible}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export const ModalSaveScoringResults = memo(ModalSaveScoringResultsBase);